import noop from 'lodash/noop';

import config from 'Config';
import { LOGIN_TYPE_FACEBOOK } from 'Lib/constants/loginTypes';
import type { Store } from 'Lib/reduxType';
import { localStorage as storage } from 'Lib/storage';
import loadScript from 'Lib/utils/loadScript';

import { facebookInitialized, facebookSdkBlocked } from 'Store/facebook/actions';

import { trackEvent } from 'Store/sparks/actions';
import { OAUTH_ERROR, OAUTH_PRESENT_UI, OAUTH_SUCCESS } from 'Store/sparks/sparks.events';

import { debugFBAPI as debug } from './debug';
import {
  FacebookAPIError,
  FacebookSDKUninitializedError,
  FacebookTimeoutError,
  LoggedOutFacebookError,
} from './errors';
import { getLocalizedScriptURL } from './FBLocaleMap';

const { fbSDK, facebookAppSettings, fbPermissionsScope } = config;
export const FbAPITokenKey = 'TinderWeb/FbAPIToken';

const hasFBSdk = () => typeof FB !== 'undefined';

// https://developers.facebook.com/docs/graph-api/guides/error-handling
interface OAuthError extends Error {
  message: string;
  type: string;
  code: number;
}

type OAuthErrorType = {
  error: OAuthError;
};
class FbAPIClient {
  initialized: boolean;
  FbAPIToken: string | null;
  _dispatch: Store['dispatch'] | null;
  locale: string | null;

  constructor() {
    this.initialized = false;
    this.FbAPIToken = null;
    this._dispatch = null;
    this.locale = null;
  }

  setDispatch(dispatch) {
    this._dispatch = dispatch || noop;
  }

  /**
   * 1. load Facebook sdk
   * 2. read FbAPITokenKey
   * 3. set initialized flag
   * 4. dispatch facebookInitialized
   */
  async initialize(locale = 'en-US') {
    const isNewLocale = this.locale !== locale;
    if (this.initialized) {
      if (!isNewLocale) {
        return;
      }
      const windowRef = window as Partial<typeof window>;
      // user change langrage on client side
      // re-initialized Facebook sdk
      delete windowRef.fbAsyncInit;
      delete windowRef.FB;
    }
    this.locale = locale;

    const FacebookSDKInitializedPromise = new Promise<void>((resolve, reject) => {
      // https://developers.facebook.com/docs/javascript/quickstart
      window.fbAsyncInit = () => {
        try {
          FB.init(facebookAppSettings);
        } catch (e) {
          reject(e);
        }
        resolve();
      };
    });

    try {
      await loadScript(getLocalizedScriptURL(fbSDK, locale), `facebook-jssdk-${locale}`);
      await FacebookSDKInitializedPromise;
    } catch (e) {
      // Facebook sdk being blocked
      this._dispatch?.(facebookSdkBlocked());
    }

    this.FbAPIToken = storage.getItem(FbAPITokenKey);
    this.initialized = true;

    this._dispatch?.(facebookInitialized());
    debug('FACEBOOK SDK LOADED');
  }

  /**
   * 1. get login status
   * 2. check if login status is valid
   * 3. throw error is status is not valid
   * 4. set FbAPIToken retrieved from response into local storage
   */
  async updateFbAPIToken() {
    const loginStatus = await this.getLoginStatus();

    if (!loginStatus.authResponse || loginStatus.status !== 'connected') {
      debug('User logged out facebook', loginStatus.status);
      throw new LoggedOutFacebookError();
    }
    this.FbAPIToken = loginStatus?.authResponse?.accessToken ?? null;
    storage.setItem(FbAPITokenKey, this.FbAPIToken || '');
  }

  async getLoginStatus(): Promise<facebook.StatusResponse> {
    return new Promise((resolve, reject) => {
      if (!hasFBSdk()) {
        return reject(new FacebookSDKUninitializedError());
      }

      FB.getLoginStatus((response) => {
        // Facebook could return oAuth error, but it't is not defined in the type definition
        const { error } = (response as unknown as OAuthErrorType) || {};
        if (error) {
          return reject(error);
        }
        return resolve(response);
      });
      return null;
    });
  }

  async login() {
    return new Promise<fb.StatusResponse>((resolve, reject) => {
      if (!hasFBSdk()) {
        return reject(new FacebookSDKUninitializedError());
      }

      this._dispatch?.(trackEvent(OAUTH_PRESENT_UI, { method: LOGIN_TYPE_FACEBOOK }));

      FB.login(
        (response) => {
          const { error } = (response as unknown as OAuthErrorType) || {};
          const { authResponse } = response || {};
          if (!authResponse || error) {
            this._dispatch?.(trackEvent(OAUTH_ERROR, { method: LOGIN_TYPE_FACEBOOK }));
            return reject(error || new FacebookAPIError());
          }

          this.FbAPIToken = authResponse.accessToken;
          storage.setItem(FbAPITokenKey, this.FbAPIToken || '');
          this._dispatch?.(trackEvent(OAUTH_SUCCESS, { method: LOGIN_TYPE_FACEBOOK }));
          return resolve(response);
        },
        { scope: fbPermissionsScope }
      );
      return null;
    });
  }

  async logout() {
    // fixes this: http://stackoverflow.com/questions/19842610/fb-logout-called-without-an-access-token-javascript-sdk
    const loginStatus = await this.getLoginStatus();
    // don't call logout if not logged in
    if (loginStatus?.status !== 'connected') return {};

    return new Promise((resolve, reject) => {
      if (!hasFBSdk()) {
        return reject(new FacebookSDKUninitializedError());
      }
      let logoutTimeout;

      if (__DEVELOPMENT__) {
        logoutTimeout = setTimeout(() => {
          reject(new FacebookTimeoutError());
        }, 2000);
      }

      FB.logout((response) => {
        if (__DEVELOPMENT__) {
          clearTimeout(logoutTimeout);
        }

        this.FbAPIToken = '';
        storage.setItem(FbAPITokenKey, '');
        const { error } = (response as unknown as OAuthErrorType) || {};
        if (error) {
          return reject(error);
        }
        return resolve(response);
      });
      return null;
    });
  }

  async authedApi(path, method: 'get' | 'post' | 'delete' = 'get', retry = true) {
    if (!this.FbAPIToken) {
      throw new FacebookSDKUninitializedError();
    }
    const FbAPIResponse = await new Promise((resolve) =>
      FB.api(path, method, { access_token: this.FbAPIToken }, (response) => resolve(response))
    );
    const { error } = (FbAPIResponse as unknown as OAuthErrorType) || {};

    if (error) {
      // debug('Got error', FbAPIResponse.error, 'retry', retry);
      if (!retry) {
        throw new FacebookAPIError(error);
      }

      if (error?.type === 'OAuthException') {
        await this.updateFbAPIToken();
        // one retry on token error
        return this.authedApi(path, method, false);
      }
      throw new FacebookAPIError(error);
    }
    return FbAPIResponse;
  }

  getToken() {
    return this.FbAPIToken;
  }

  loadEmail() {
    return this.authedApi('/me?fields=email');
  }
}

export default new FbAPIClient();
